import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { buildBasicFeatureStats, buildBonusStats } from '../lib/buildBasicFeatureStats';
import { calculateGroupScore } from '../lib/calculateGroupScore';
import { MAX_RUBRIC_SCORE } from '../config';

// ── Score distribution histogram ───────────────────────────────────────────
const BUCKETS = [
  { label: '0–19',    min: 0,   max: 20  },
  { label: '20–39',   min: 20,  max: 40  },
  { label: '40–59',   min: 40,  max: 60  },
  { label: '60–79',   min: 60,  max: 80  },
  { label: '80–99',   min: 80,  max: 100 },
  { label: '100–119', min: 100, max: 120 },
  { label: '120–139', min: 120, max: 140 },
  { label: '140–159', min: 140, max: 160 },
  { label: '160–179', min: 160, max: 180 },
  { label: '180–200', min: 180, max: 201 },
];

function ScoreHistogram({ groups }) {
  const buckets = useMemo(() => {
    const counts = BUCKETS.map((b) => ({ ...b, count: 0 }));
    groups.forEach((g) => {
      const score = calculateGroupScore(g);
      const idx = counts.findIndex((b) => score >= b.min && score < b.max);
      if (idx !== -1) counts[idx].count += 1;
    });
    return counts;
  }, [groups]);

  const maxCount = Math.max(...buckets.map((b) => b.count), 1);
  const BAR_MAX_H = 120;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
        Score Distribution
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Number of groups per score range (out of {MAX_RUBRIC_SCORE})
      </Typography>
      <Box display="flex" alignItems="flex-end" gap={1} sx={{ overflowX: 'auto', pb: 1 }}>
        {buckets.map((b) => {
          const barH = b.count === 0 ? 4 : Math.max(4, (b.count / maxCount) * BAR_MAX_H);
          const isEmpty = b.count === 0;
          return (
            <Tooltip key={b.label} title={`${b.count} group${b.count !== 1 ? 's' : ''}`} arrow>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: '1 1 0',
                  minWidth: 44,
                  cursor: 'default',
                }}
              >
                <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
                  {isEmpty ? '' : b.count}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: barH,
                    bgcolor: isEmpty ? '#e0e0e0' : 'primary.main',
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s',
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, fontSize: '0.65rem', textAlign: 'center', lineHeight: 1.2 }}
                >
                  {b.label}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Paper>
  );
}

// ── Category breakdown ──────────────────────────────────────────────────────
function CategoryBreakdown({ groups }) {
  const rows = useMemo(() => {
    const map = {};
    groups.forEach((g) => {
      (g.basicFeatures || []).forEach((f) => {
        const F = parseFloat(f.functionality);
        const A = parseFloat(f.aesthetics);
        if (isNaN(F) || isNaN(A)) return;
        const cat = f.category || 'Uncategorized';
        const worth = parseFloat(f.worth) || 0;
        const aesWeight = f.aestheticsBonus ? 0.3 : 0.2;
        const earned = Math.min(worth * ((0.8 * F + aesWeight * A) / 100), worth);
        if (!map[cat]) map[cat] = { earned: 0, worth: 0 };
        map[cat].earned += earned;
        map[cat].worth += worth;
      });
    });
    return Object.entries(map)
      .map(([cat, { earned, worth }]) => ({
        category: cat,
        pct: worth > 0 ? (earned / worth) * 100 : 0,
        earned: earned.toFixed(1),
        worth: worth.toFixed(1),
      }))
      .sort((a, b) => b.pct - a.pct);
  }, [groups]);

  if (!rows.length) return null;

  const barColor = (pct) => {
    if (pct >= 75) return '#4caf50';
    if (pct >= 50) return '#ff9800';
    return '#f44336';
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
        Category Breakdown
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Average points earned per category across all graded groups
      </Typography>
      <Box display="flex" flexDirection="column" gap={1.5}>
        {rows.map((row) => (
          <Box key={row.category}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="body2" fontWeight="bold">
                {row.category}
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {row.earned} / {row.worth} pts
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ minWidth: 44, textAlign: 'right', color: barColor(row.pct) }}
                >
                  {row.pct.toFixed(1)}%
                </Typography>
              </Box>
            </Box>
            <Box sx={{ height: 12, bgcolor: '#e0e0e0', borderRadius: 6, overflow: 'hidden' }}>
              <Box
                sx={{
                  width: `${row.pct}%`,
                  height: '100%',
                  bgcolor: barColor(row.pct),
                  borderRadius: 6,
                  transition: 'width 0.4s ease',
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
      <Box display="flex" gap={3} mt={2}>
        {[['≥75%', '#4caf50', 'Strong'], ['50–74%', '#ff9800', 'Moderate'], ['<50%', '#f44336', 'Weak']].map(
          ([label, color, text]) => (
            <Box key={label} display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: '50%' }} />
              <Typography variant="caption" color="text.secondary">
                {label} — {text}
              </Typography>
            </Box>
          )
        )}
      </Box>
    </Paper>
  );
}

function SortableFeatureTable({ title, rows, accentColor }) {
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(
    () => [...rows].sort((a, b) =>
      sortDir === 'desc'
        ? parseFloat(b.avg) - parseFloat(a.avg)
        : parseFloat(a.avg) - parseFloat(b.avg)
    ),
    [rows, sortDir]
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
        {title}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="50%">Feature</TableCell>
              <TableCell sortDirection={sortDir}>
                <TableSortLabel
                  active
                  direction={sortDir}
                  onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
                >
                  Average Score (0–100)
                </TableSortLabel>
              </TableCell>
              <TableCell>Max Worth</TableCell>
              <TableCell>Graded Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{row.name}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <CircularProgress
                      variant="determinate"
                      value={parseFloat(row.avg)}
                      size={20}
                      sx={{ mr: 1, color: accentColor }}
                    />
                    <strong>{row.avg}</strong>
                  </Box>
                </TableCell>
                <TableCell>{row.worth}</TableCell>
                <TableCell>{row.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export function AnalysisView({ groups }) {
  const navigate = useNavigate();
  const basicRows = useMemo(() => buildBasicFeatureStats(groups), [groups]);
  const bonusRows = useMemo(() => buildBonusStats(groups), [groups]);

  const overallAvg = useMemo(() => {
    if (!groups.length) return null;
    const total = groups.reduce((acc, g) => acc + calculateGroupScore(g), 0);
    return (total / groups.length).toFixed(2);
  }, [groups]);

  const taStats = useMemo(() => {
    const map = {};
    groups.forEach((g) => {
      const ta = g.createdBy || 'Unassigned';
      if (!map[ta]) map[ta] = { total: 0, count: 0 };
      map[ta].total += calculateGroupScore(g);
      map[ta].count += 1;
    });
    return Object.entries(map)
      .map(([ta, { total, count }]) => ({ ta, count, avg: (total / count).toFixed(2) }))
      .sort((a, b) => parseFloat(b.avg) - parseFloat(a.avg));
  }, [groups]);

  return (
    <div style={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Grading Analysis
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>

        {/* ── Summary cards ─────────────────────────────────────── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="overline" color="text.secondary">
                Total Groups
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="primary">
                {groups.length}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="overline" color="text.secondary">
                Overall Avg Score
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="success.main">
                {overallAvg ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                out of {MAX_RUBRIC_SCORE}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="overline" color="text.secondary">
                TAs Grading
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="primary">
                {taStats.length}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* ── Per-TA averages ────────────────────────────────────── */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Average Score per TA
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TA</TableCell>
                  <TableCell>Groups Graded</TableCell>
                  <TableCell>Avg Final Score</TableCell>
                  <TableCell width="40%">Distribution</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taStats.map((row) => (
                  <TableRow key={row.ta}>
                    <TableCell>
                      <strong>{row.ta}</strong>
                    </TableCell>
                    <TableCell>
                      <Chip label={row.count} size="small" sx={{ bgcolor: '#eee' }} />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="success.main">
                        {row.avg} / {MAX_RUBRIC_SCORE}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            flexGrow: 1,
                            height: 10,
                            bgcolor: '#e0e0e0',
                            borderRadius: 5,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${(parseFloat(row.avg) / MAX_RUBRIC_SCORE) * 100}%`,
                              height: '100%',
                              bgcolor: 'success.main',
                              borderRadius: 5,
                            }}
                          />
                        </Box>
                        <Typography variant="caption">
                          {((parseFloat(row.avg) / MAX_RUBRIC_SCORE) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {taStats.length > 1 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary">
                  Overall average:
                </Typography>
                <Typography fontWeight="bold" color="success.main">
                  {overallAvg} / {MAX_RUBRIC_SCORE}
                </Typography>
              </Box>
            </>
          )}
        </Paper>

        {/* ── Score distribution ────────────────────────────────── */}
        <ScoreHistogram groups={groups} />

        {/* ── Category breakdown ────────────────────────────────── */}
        <CategoryBreakdown groups={groups} />

        {/* ── Feature tables ─────────────────────────────────────── */}
        <SortableFeatureTable
          title="Basic Features Average Scores"
          rows={basicRows}
          accentColor="success.main"
        />
        <Box mt={3}>
          <SortableFeatureTable
            title="Bonus (optional) Average Scores"
            rows={bonusRows}
            accentColor="warning.main"
          />
        </Box>
      </Container>
    </div>
  );
}
